import { ShieldAlert, Menu, X } from "lucide-react";
import { useEffect, useState } from "react";
import { getAccessToken } from "@/lib/storeManager";
import { Button } from "@/projects/NPS/Stepper/common/button";
import { useNavigate, useLocation } from "react-router-dom";
import FinEaseHomeRoutes from "../../utils/navigationRoutes";
import CommonModal from "../../common/Modal";
import OtpVerification from "@/projects/NPS/Stepper/pages/OtpVerification";

export function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const isLoggedIn = getAccessToken() !== null;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLogoutOpen, setIsLogoutOpen] = useState(false);
  const getMobile = localStorage.getItem("userMobile");
  const [isOtpOpen, setIsOtpOpen] = useState(false);
  const NPSLogo = "/assets/NPSLogo.png";

  // Check if a nav link is active based on current pathname
  const isActiveLink = (link: string) => {
    if (!link || link === "#" || link.startsWith("http")) {
      return false;
    }
    return (
      location.pathname === link || location.pathname.startsWith(link + "/")
    );
  };

  // Base nav items (always visible)
  const baseNavItems = [
    { label: "IPO", link: `/ipo`, external: false },
    { label: "Bond", link: `/bonds`, external: false },
    { label: "FD", link: `/fds`, external: false },
    { label: "NPS", link: `/nps`, external: false },
    { label: "Calculators", link: `/calculators`, external: false },
    {
      label: "Screener",
      link: "https://www.thefinease.com/",
      external: true,
    },
  ];

  // Add Dashboard link if user is logged in
  const desktopNavItems = isLoggedIn
    ? [
        { label: "Dashboard", link: `/nps-user/nps-overview`, external: false },
        ...baseNavItems,
      ]
    : baseNavItems;

  // const { tenantId } = useTenant();

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsMobileMenuOpen(false);
      }
    };
    if (isMobileMenuOpen) {
      document.addEventListener("keydown", handleEscape);
    }
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isMobileMenuOpen]);

  const handleLoginSignup = () => {
    setIsMobileMenuOpen(true);
    setIsOtpOpen(true);
  };

  const handleLogout = () => {
    localStorage.clear();
    setIsLogoutOpen(false);
    window.location.reload();
  };

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-white px-4 py-1 shadow-sm md:py-0 lg:px-10">
      <div className="container mx-auto w-full">
        <div className="flex h-16 items-center justify-between sm:h-20 lg:h-20">
          {/* Logo */}
          <div className="flex items-center space-x-2 sm:space-x-3 lg:space-x-4">
            <img className="w-full" src={NPSLogo} alt="Fincare Logo" />
          </div>

          {/* Desktop Nav */}
          <nav className="hidden items-center gap-1 lg:flex">
            {desktopNavItems.map(item => {
              const isActive = !item.external && isActiveLink(item.link);
              return (
                <a
                  key={item.label}
                  href={item.link}
                  {...(item.external
                    ? { target: "_blank", rel: "noopener noreferrer" }
                    : {})}
                  className={`px-4 py-2 text-[14px] font-semibold transition-colors ${
                    isActive
                      ? "text-[#1AAE90]"
                      : "text-muted-foreground hover:text-primary"
                  }`}
                >
                  {item.label}
                </a>
              );
            })}
          </nav>

          {/* Search and Auth */}
          <div className="hidden items-center gap-4 md:flex">
            <div className="flex h-10 w-[240px] items-center gap-2 rounded-lg border border-[rgba(229,232,235,0.6)] bg-[rgba(238,240,242,0.4)] px-4 transition-all duration-200 focus-within:border-[rgba(29,171,145,0.4)] focus-within:bg-white focus-within:shadow-sm lg:w-[280px]">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#656F75"
                strokeWidth="2"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
              </svg>

              <input
                type="text"
                placeholder="Search IPO, Bonds, FDs..."
                className="flex-1 border-none bg-transparent text-[14px] text-foreground outline-none placeholder:text-muted-foreground"
              />
            </div>

            {!isLoggedIn && (
              <Button
                onClick={() => navigate(FinEaseHomeRoutes.ADMIN)}
                variant="secondary"
                className="cursor-pointer items-center gap-2 rounded-lg bg-secondary px-4 py-2 text-sm font-bold text-white hover:bg-secondary/90 xl:flex"
              >
                <ShieldAlert className="h-4 w-4" />
                Admin
              </Button>
            )}

            <div className="flex items-center justify-center space-x-3 py-2">
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#E4FFFB] p-2">
                <img
                  className="h-5 w-5 text-slate-600"
                  src={"/assets/user.svg"}
                  alt="User Icon"
                />
              </div>
              {!isLoggedIn ? (
                <button
                  type="button"
                  className="font-nunito text-base font-bold text-[#707070] transition-colors hover:text-[#266985]"
                  onClick={handleLoginSignup}
                >
                  Login / Sign up
                </button>
              ) : (
                <button
                  type="button"
                  className="font-nunito text-base font-bold text-[#707070] transition-colors hover:text-[#266985]"
                  onClick={() => setIsLogoutOpen(!isLogoutOpen)}
                >
                  {getMobile}
                </button>
              )}
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="bg-[#E4FFFB] p-2 md:hidden"
            onClick={toggleMobileMenu}
          >
            {isMobileMenuOpen ? (
              <X className="h-10 w-10 text-[#266985]" />
            ) : (
              <Menu className="h-10 w-10 text-[#266985]" />
            )}
          </Button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <nav className="border-t border-slate-200 bg-white md:hidden">
            <div className="space-y-2 px-3 pb-3 pt-3 sm:px-4 sm:pb-4 sm:pt-4">
              <div className="flex flex-col items-center justify-center border-b border-slate-100 pb-3">
                {[
                  ...(isLoggedIn
                    ? [
                        {
                          name: "Dashboard",
                          link: `/dashboard`,
                          external: false,
                        },
                      ]
                    : []),
                  { name: "IPO", link: `/ipo`, external: false },
                  { name: "Bonds", link: `/bonds`, external: false },
                  { name: "FD", link: `/fds`, external: false },
                  { name: "NPS", link: `/nps`, external: false },
                  {
                    name: "Calculators",
                    link: `/calculators`,
                    external: false,
                  },
                  {
                    name: "Screener",
                    link: "https://www.thefinease.com/",
                    external: true,
                  },
                ].map(link => {
                  const isActive = !link.external && isActiveLink(link.link);
                  return (
                    <a
                      key={link.name}
                      href={link.external ? link.link : link.link}
                      {...(link.external
                        ? { target: "_blank", rel: "noopener noreferrer" }
                        : {})}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`py-2 text-sm font-medium transition-colors ${
                        isActive
                          ? "text-[#1AAE90]"
                          : "text-muted-foreground hover:text-primary"
                      }`}
                    >
                      {link.name}
                    </a>
                  );
                })}
              </div>

              <div className="flex flex-col items-center justify-center space-y-3 py-2 md:flex md:flex-row md:items-center md:justify-center md:space-x-3 md:space-y-0">
                {/* Search Bar */}
                <div className="flex h-10 w-[240px] items-center gap-2 rounded-lg border border-[rgba(229,232,235,0.6)] bg-[rgba(238,240,242,0.4)] px-4 transition-all duration-200 focus-within:border-[rgba(29,171,145,0.4)] focus-within:bg-white focus-within:shadow-sm lg:w-[280px]">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#656F75"
                    strokeWidth="2"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.3-4.3" />
                  </svg>

                  <input
                    type="text"
                    placeholder="Search IPO, Bonds, FDs..."
                    className="flex-1 border-none bg-transparent text-[14px] text-foreground outline-none placeholder:text-muted-foreground"
                  />
                </div>

                {/* User Icon */}
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#E4FFFB] p-2">
                  <img
                    className="h-5 w-5"
                    src={"/assets/user.svg"}
                    alt="User Icon"
                  />
                </div>

                {/* Login Button */}
                {!isLoggedIn ? (
                  <button
                    type="button"
                    className="font-nunito text-base font-bold text-[#707070] transition-colors hover:text-[#266985]"
                    onClick={handleLoginSignup}
                  >
                    Login / Sign up
                  </button>
                ) : (
                  <button
                    type="button"
                    className="font-nunito text-base font-bold text-[#707070] transition-colors hover:text-[#266985]"
                    onClick={() => setIsLogoutOpen(true)}
                  >
                    {getMobile}
                  </button>
                )}
              </div>
            </div>
          </nav>
        )}
      </div>

      <CommonModal
        isOpen={isOtpOpen}
        onClose={() => setIsOtpOpen(false)}
        width="w-full sm:w-[600px]"
        height="h-auto"
        padding="p-2 sm:p-6"
      >
        <OtpVerification
          onClose={() => setIsOtpOpen(false)}
          onSuccess={() => {
            setIsOtpOpen(false);
            window.location.reload();
          }}
        />
      </CommonModal>
      <div
        className={`fixed inset-0 z-50 flex items-end justify-center bg-[#00000080] backdrop-blur-sm transition-all duration-300 md:hidden ${
          isLogoutOpen ? "visible opacity-100" : "invisible opacity-0"
        }`}
        onClick={() => setIsLogoutOpen(false)}
      >
        <div
          className={`mx-4 mb-4 w-full transform rounded-t-2xl bg-[#E4FFFB] shadow-xl transition-all duration-300 ${
            isLogoutOpen ? "translate-y-0" : "translate-y-full"
          }`}
          onClick={e => e.stopPropagation()}
        >
          <div className="space-y-4 p-6 text-center">
            <h2 className="font-nunito text-lg font-bold text-[#266985]">
              Are you sure you want to log out?
            </h2>
            <div className="mt-4 flex justify-center space-x-4">
              <button
                onClick={() => setIsLogoutOpen(false)}
                className="rounded-md border border-[#266985] px-5 py-2 font-nunito font-semibold text-[#266985] transition hover:bg-[#d3f5f0]"
              >
                Cancel
              </button>
              <button
                onClick={handleLogout}
                className="rounded-md bg-gradient-to-l from-[#1AAE90] to-[#258996] px-5 py-2 font-nunito font-semibold text-white transition hover:bg-[#1f5870]"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Logout Modal - Right Side with Animation */}
      <div
        className={`fixed right-0 z-50 hidden transition-all duration-300 md:block ${
          isLogoutOpen
            ? "top-28 opacity-100"
            : "pointer-events-none -top-full opacity-0"
        }`}
      >
        <div
          className={`mr-4 w-[400px] transform rounded-lg bg-[#E4FFFB] shadow-xl transition-all duration-300 md:mr-6 lg:mr-10 ${
            isLogoutOpen
              ? "translate-y-0 opacity-100"
              : "-translate-y-4 opacity-0"
          }`}
          onClick={e => e.stopPropagation()}
        >
          <div className="space-y-4 p-6 text-center">
            <h2 className="font-nunito text-lg font-bold text-[#266985]">
              Are you sure you want to log out?
            </h2>
            <div className="mt-4 flex justify-center space-x-4">
              <button
                onClick={() => setIsLogoutOpen(false)}
                className="rounded-md border border-[#266985] px-5 py-2 font-nunito font-semibold text-[#266985] transition hover:bg-[#d3f5f0]"
              >
                Cancel
              </button>
              <button
                onClick={handleLogout}
                className="rounded-md bg-gradient-to-l from-[#1AAE90] to-[#258996] px-5 py-2 font-nunito font-semibold text-white transition hover:bg-[#1f5870]"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Click outside to close dropdown - Desktop only */}
      {isLogoutOpen && (
        <div
          className="fixed inset-0 z-40 hidden bg-[#00000080] backdrop-blur-sm transition-all duration-300 md:block"
          onClick={() => setIsLogoutOpen(false)}
        />
      )}
    </header>
  );
}
